// Shared data hooks for dashboard header widgets.
// These are the single source of truth for weather, system health, quote-of-day,
// and live time. Both the original mini widgets and the new AIChadGreeting banner
// consume these hooks so there are no duplicate fetches.
//
// Behavior for each hook matches the original in-widget implementation byte-for-byte:
//   - useWeather         → same parse/polling as WeatherWidgetMini   (30 min interval, /api/weather)
//   - useSystemHealth    → same parse/polling as SystemHealthIndicator (5 min interval, /api/system/health)
//   - useQuoteOfDay      → same dayOfYear % DAILY_QUOTES.length logic as QuoteWidgetMini
//   - useLiveTime        → same 1000ms tick as ClockWidgetMini
import { useState, useEffect, useCallback } from 'react';
import { DAILY_QUOTES } from '@/components/dashboard/DayStartGuide';
import { getCurrentUserId } from '../utils/currentUser';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to get weather emoji from code — mirrors WeatherWidgets.js getWeatherEmoji.
export const getWeatherEmoji = (code) => {
  const codeNum = parseInt(code);
  if (codeNum === 113) return '☀️';
  if (codeNum === 116) return '⛅';
  if ([119, 122].includes(codeNum)) return '☁️';
  if ([176, 263, 266, 293, 296, 299, 302, 305, 308, 311, 314, 317, 353, 356, 359].includes(codeNum)) return '🌧️';
  if ([179, 182, 185, 227, 230, 320, 323, 326, 329, 332, 335, 338, 350, 362, 365, 368, 371, 374, 377].includes(codeNum)) return '🌨️';
  if ([200, 386, 389, 392, 395].includes(codeNum)) return '⛈️';
  return '🌤️';
};

/**
 * useWeather — fetches current conditions + forecast high/low.
 * Returns: { temp, conditions, code, emoji, high, low, location, loading, error }
 */
export function useWeather() {
  const [state, setState] = useState({
    temp: null,
    conditions: '',
    code: null,
    location: '',
    high: '--',
    low: '--',
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    // Feb 2026 fix — request browser geolocation so the weather report is
    // for the USER's location, not the server's IP geolocation (which on
    // Emergent's cluster sits in Council Bluffs / Iowa for every tenant).
    const getCoords = () =>
      new Promise((resolve) => {
        if (!navigator.geolocation) return resolve(null);
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
          () => resolve(null),
          { timeout: 4000, maximumAge: 1000 * 60 * 60 },
        );
      });

    const fetchWeather = async () => {
      try {
        const coords = await getCoords();
        const qs = coords ? `?lat=${coords.lat}&lon=${coords.lon}` : '';
        const response = await axios.get(`${API_URL}/api/weather${qs}`);
        if (!(response && response.status >= 200 && response.status < 300)) throw new Error(`Weather API returned ${response.status}`);
        const rawData = response.data;
        const data = rawData.data || rawData;
        if (data.error || !data.current_condition || !data.current_condition[0]) {
          throw new Error(data.error || 'Invalid weather data');
        }
        if (cancelled) return;
        setState({
          temp: data.current_condition[0].temp_F,
          conditions: data.current_condition[0].weatherDesc[0].value,
          code: data.current_condition[0].weatherCode,
          location: data.nearest_area?.[0]?.areaName?.[0]?.value || '',
          high: data.weather?.[0]?.maxtempF || '--',
          low: data.weather?.[0]?.mintempF || '--',
          loading: false,
          error: null,
        });
      } catch (error) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('Weather fetch error:', error);
        setState({
          temp: '--',
          conditions: 'Weather unavailable',
          code: '113',
          location: '',
          high: '--',
          low: '--',
          loading: false,
          error,
        });
      }
    };
    fetchWeather();
    const interval = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  return { ...state, emoji: getWeatherEmoji(state.code) };
}

/**
 * useSystemHealth — fetches overall health and per-service status.
 * Returns: { health, status, loading, error, refetch }
 *   status is the normalized `'healthy' | 'warning' | 'down'` (mapping 'error' → 'down')
 *   health is the full raw response object (services, issues, overall)
 */
export function useSystemHealth(userId = getCurrentUserId()) {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchHealth = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/system/health?user_id=${userId}`);
      if ((res && res.status >= 200 && res.status < 300)) {
        const data = res.data;
        setHealth(data);
        setError(null);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Health check failed:', err);
      setHealth({ overall: 'error', issues: [{ service: 'System', message: 'Health check failed', severity: 'error' }] });
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  // Normalize the overall status into the public 3-value enum expected by callers.
  const overall = health?.overall;
  let status = 'healthy';
  if (overall === 'warning') status = 'warning';
  else if (overall === 'error' || overall === 'down') status = 'down';

  return { health, status, loading, error, refetch: fetchHealth };
}

/**
 * useQuoteOfDay — returns the quote of the day using the same dayOfYear logic
 * as QuoteWidgetMini. Re-reads quote when the day rolls over if the component
 * stays mounted past midnight (not strictly required but defensive).
 */
export function useQuoteOfDay() {
  const compute = () => {
    const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
    const quoteIndex = dayOfYear % DAILY_QUOTES.length;
    return DAILY_QUOTES[quoteIndex];
  };
  const [quote, setQuote] = useState(compute);

  useEffect(() => {
    // Tick once an hour to catch the midnight rollover without a busy timer.
    const id = setInterval(() => setQuote(compute()), 60 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  return quote;
}

/**
 * useLiveTime — returns the current Date ticking every 1000ms.
 */
export function useLiveTime() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return { now };
}
